/**
 * Original, procedural NYC-inspired scene kit. Three.js primitives only.
 * No DOM, network, textures, font files, downloaded models, or branded assets.
 * Units: metres; Y up; building fronts face +Z; avatar front faces +Z.
 * Scene lighting/camera/renderer are deliberately owned by the host application.
 * Uses the public Three.js Group, Mesh, InstancedMesh and primitive geometry APIs.
 * Reference: https://threejs.org/docs/ (Three.js is MIT licensed).
 */
import * as THREE from 'three';

export type Rect = { minX: number; maxX: number; minZ: number; maxZ: number };
export type LocationAnchor = { id: string; label: string; position: [number, number, number] };
export type AvatarOptions = {
  skin?: THREE.ColorRepresentation;
  shirt?: THREE.ColorRepresentation;
  hair?: THREE.ColorRepresentation;
  trousers?: THREE.ColorRepresentation;
};

export const NYC_PALETTE = {
  brick: 0xb7684e,
  brickLight: 0xcc8567,
  brickDark: 0x8b5142,
  sandstone: 0xd8b48b,
  cream: 0xf4e4c3,
  teal: 0x367f78,
  tealDark: 0x245952,
  iron: 0x344442,
  glass: 0x728e95,
  glassLight: 0xa7bdba,
  asphalt: 0x596264,
  sidewalk: 0xd2c3aa,
  wood: 0xa8774c,
  foliage: 0x819451,
  foliageLight: 0xa6b965,
  river: 0x73a7ae,
  taxi: 0xf0b83d,
  red: 0xc65942,
};

/** Safe spawn/interaction positions are intentionally separate from scenery. */
export const NYC_MAP = {
  spawn: [0, 0.04, 3] as [number, number, number],
  bounds: { minX: -25.5, maxX: 25.5, minZ: -16, maxZ: 12.2 } as Rect,
  walkable: [
    { minX: -16, maxX: 24.8, minZ: -4.2, maxZ: 6.2 },
    { minX: -25.4, maxX: 25.4, minZ: 5.4, maxZ: 12 },
    { minX: -25.4, maxX: -23.4, minZ: -15.8, maxZ: 12 },
    { minX: 22.1, maxX: 25.4, minZ: -15.8, maxZ: 12 },
    { minX: -25.4, maxX: 25.4, minZ: -15.8, maxZ: -14.1 },
  ] as Rect[],
  blocked: [
    { minX: -24.3, maxX: -17.4, minZ: -1.6, maxZ: 5.4 },
    { minX: 0.3, maxX: 3.8, minZ: -1.7, maxZ: 1.7 },
    { minX: 9.4, maxX: 11.1, minZ: 0.9, maxZ: 2.6 },
    { minX: 15.9, maxX: 17.5, minZ: 0.2, maxZ: 1.8 },
    { minX: -9.8, maxX: -6, minZ: 8.6, maxZ: 10.4 },
  ] as Rect[],
  locations: [
    { id: 'home', label: 'Brownstone Apartments', position: [-13.5, 0, -3.5] },
    { id: 'cafe', label: 'Corner Café', position: [-7, 0, -3.5] },
    { id: 'bodega', label: 'Neighborhood Market', position: [0, 0, -3.5] },
    { id: 'pizza', label: 'Slice Shop', position: [6.2, 0, -3.5] },
    { id: 'jobs', label: 'Career Center', position: [16, 0, -3.5] },
    { id: 'city-hall', label: 'Civic Hall', position: [-15.7, 0, 2] },
    { id: 'subway', label: 'Subway', position: [2, 0, 2.6] },
    { id: 'park', label: 'Riverside Pocket Park', position: [12, 0, 4.3] },
  ] as LocationAnchor[],
};

const geometries = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cylinder: new THREE.CylinderGeometry(1, 1, 1, 8),
  sphere: new THREE.IcosahedronGeometry(1, 1),
  leaf: new THREE.IcosahedronGeometry(1, 0),
  cone: new THREE.ConeGeometry(1, 1, 8),
  pyramid: new THREE.ConeGeometry(1, 1, 4),
  head: new THREE.SphereGeometry(1, 10, 7),
  hair: new THREE.SphereGeometry(1, 10, 4, 0, Math.PI * 2, 0, Math.PI * 0.53),
};
const materials = new Map<string, THREE.MeshStandardMaterial>();
function mat(color: THREE.ColorRepresentation, glow = false): THREE.MeshStandardMaterial {
  const normalized = new THREE.Color(color);
  const key = `${normalized.getHexString()}:${glow}`;
  let result = materials.get(key);
  if (!result) {
    result = new THREE.MeshStandardMaterial({
      color: normalized,
      roughness: 0.88,
      metalness: 0,
      ...(glow ? { emissive: normalized, emissiveIntensity: 0.3 } : {}),
    });
    materials.set(key, result);
  }
  return result;
}
function group(name: string): THREE.Group {
  const g = new THREE.Group();
  g.name = name;
  return g;
}
function mesh(
  parent: THREE.Group,
  shape: keyof typeof geometries,
  color: THREE.ColorRepresentation,
  position: [number, number, number],
  scale: [number, number, number],
  glow = false,
): THREE.Mesh {
  const m = new THREE.Mesh(geometries[shape], mat(color, glow));
  m.position.set(...position);
  m.scale.set(...scale);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
  return m;
}
function box(
  g: THREE.Group,
  color: THREE.ColorRepresentation,
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  d: number,
  glow = false,
): THREE.Mesh {
  return mesh(g, 'box', color, [x, y, z], [w, h, d], glow);
}
function cylinder(
  g: THREE.Group,
  color: THREE.ColorRepresentation,
  x: number,
  y: number,
  z: number,
  radius: number,
  height: number,
): THREE.Mesh {
  return mesh(g, 'cylinder', color, [x, y, z], [radius, height, radius]);
}
function beam(
  g: THREE.Group,
  color: THREE.ColorRepresentation,
  from: [number, number, number],
  to: [number, number, number],
  radius = 0.045,
): THREE.Mesh {
  const start = new THREE.Vector3(...from),
    end = new THREE.Vector3(...to);
  const delta = end.clone().sub(start);
  const m = mesh(
    g,
    'cylinder',
    color,
    start.clone().add(end).multiplyScalar(0.5).toArray() as [number, number, number],
    [radius, delta.length(), radius],
  );
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.normalize());
  return m;
}

/** Batch the static primitive meshes inside ONE asset. Asset root can still move. */
function batch(g: THREE.Group): THREE.Group {
  g.updateMatrixWorld(true);
  const inverse = g.matrixWorld.clone().invert();
  const buckets = new Map<
    string,
    { geometry: THREE.BufferGeometry; material: THREE.Material; matrices: THREE.Matrix4[] }
  >();
  const remove: THREE.Mesh[] = [];
  g.traverse((object) => {
    if (
      !(object instanceof THREE.Mesh) ||
      object instanceof THREE.InstancedMesh ||
      Array.isArray(object.material)
    )
      return;
    const key = `${object.geometry.uuid}:${object.material.uuid}`;
    let b = buckets.get(key);
    if (!b) {
      b = { geometry: object.geometry, material: object.material, matrices: [] };
      buckets.set(key, b);
    }
    b.matrices.push(new THREE.Matrix4().multiplyMatrices(inverse, object.matrixWorld));
    remove.push(object);
  });
  for (const object of remove) object.removeFromParent();
  let count = 0;
  for (const b of buckets.values()) {
    const instances = new THREE.InstancedMesh(b.geometry, b.material, b.matrices.length);
    instances.name = `${g.name}:batch:${count++}`;
    b.matrices.forEach((matrix, index) => instances.setMatrixAt(index, matrix));
    instances.instanceMatrix.needsUpdate = true;
    instances.castShadow = true;
    instances.receiveShadow = true;
    instances.computeBoundingSphere();
    g.add(instances);
  }
  return g;
}

/** Merge already-instanced static assets without losing their placed transforms. */
export function batchStaticScene(root: THREE.Group): THREE.Group {
  root.updateMatrixWorld(true);
  const inverse = root.matrixWorld.clone().invert(),
    local = new THREE.Matrix4();
  const buckets = new Map<
    string,
    {
      geometry: THREE.BufferGeometry;
      material: THREE.Material;
      matrices: THREE.Matrix4[];
      cast: boolean;
      receive: boolean;
    }
  >();
  const removed: THREE.Mesh[] = [];
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || Array.isArray(object.material)) return;
    const key = `${object.geometry.uuid}:${object.material.uuid}:${object.castShadow}:${object.receiveShadow}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        geometry: object.geometry,
        material: object.material,
        matrices: [],
        cast: object.castShadow,
        receive: object.receiveShadow,
      };
      buckets.set(key, bucket);
    }
    const placed = new THREE.Matrix4().multiplyMatrices(inverse, object.matrixWorld);
    if (object instanceof THREE.InstancedMesh) {
      for (let i = 0; i < object.count; i++) {
        object.getMatrixAt(i, local);
        bucket.matrices.push(new THREE.Matrix4().multiplyMatrices(placed, local));
      }
    } else bucket.matrices.push(placed);
    removed.push(object);
  });
  for (const object of removed) {
    object.removeFromParent();
    if (object instanceof THREE.InstancedMesh) object.dispose();
  }
  let index = 0;
  for (const bucket of buckets.values()) {
    const mesh = new THREE.InstancedMesh(bucket.geometry, bucket.material, bucket.matrices.length);
    mesh.name = `static-city:${index++}`;
    bucket.matrices.forEach((matrix, i) => mesh.setMatrixAt(i, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = bucket.cast;
    mesh.receiveShadow = bucket.receive;
    mesh.computeBoundingSphere();
    root.add(mesh);
  }
  return root;
}

function windowPanel(g: THREE.Group, x: number, y: number, z: number, width = 0.9, height = 1.3): void {
  box(g, NYC_PALETTE.sandstone, x, y, z, width + 0.24, height + 0.23, 0.18);
  box(g, NYC_PALETTE.iron, x, y, z + 0.11, width + 0.06, height + 0.03, 0.08);
  box(g, NYC_PALETTE.glass, x, y, z + 0.16, width - 0.1, height - 0.12, 0.03);
  box(g, NYC_PALETTE.iron, x, y, z + 0.19, width, 0.07, 0.04);
  box(g, NYC_PALETTE.sandstone, x, y - height / 2 - 0.14, z + 0.11, width + 0.38, 0.16, 0.36);
}
function flowerBox(g: THREE.Group, x: number, y: number, z: number): void {
  box(g, NYC_PALETTE.wood, x, y, z, 1, 0.3, 0.42);
  for (let i = 0; i < 3; i++)
    mesh(
      g,
      'leaf',
      i % 2 ? NYC_PALETTE.foliage : NYC_PALETTE.foliageLight,
      [x - 0.31 + i * 0.3, y + 0.22, z],
      [0.24, 0.32, 0.22],
    );
}
function fireEscape(g: THREE.Group, front: number, height: number, x = 1.2): void {
  const levels = Math.max(1, Math.floor((height - 2.4) / 2.5));
  for (let floor = 0; floor < levels; floor++) {
    const y = 3 + floor * 2.5;
    box(g, NYC_PALETTE.iron, x, y, front + 0.5, 1.8, 0.13, 1.1);
    for (const side of [-1, 1])
      beam(
        g,
        NYC_PALETTE.iron,
        [x + side * 0.84, y, front + 0.95],
        [x + side * 0.84, y + 0.85, front + 0.95],
      );
    beam(g, NYC_PALETTE.iron, [x - 0.86, y + 0.85, front + 0.95], [x + 0.86, y + 0.85, front + 0.95]);
    for (let j = 0; j < 7; j++)
      beam(
        g,
        NYC_PALETTE.iron,
        [x - 0.78 + j * 0.26, y, front + 0.95],
        [x - 0.78 + j * 0.26, y + 0.85, front + 0.95],
        0.025,
      );
    if (floor < levels - 1) {
      for (const offset of [-0.28, 0.28])
        beam(
          g,
          NYC_PALETTE.iron,
          [x - 0.58 + offset, y + 0.1, front + 0.65],
          [x + 0.58 + offset, y + 2.5, front + 0.65],
          0.045,
        );
      for (let j = 0; j < 8; j++) {
        const t = j / 7;
        beam(
          g,
          NYC_PALETTE.iron,
          [x - 0.58 + t * 1.16 - 0.28, y + 0.1 + t * 2.4, front + 0.65],
          [x - 0.58 + t * 1.16 + 0.28, y + 0.1 + t * 2.4, front + 0.65],
          0.025,
        );
      }
    }
  }
}

export function createBrownstone(
  options: { width?: number; height?: number; brick?: THREE.ColorRepresentation; fireEscape?: boolean } = {},
): THREE.Group {
  const g = group('brownstone');
  const w = options.width ?? 5.2,
    h = options.height ?? 9.5,
    depth = 5.8;
  const front = depth / 2;
  box(g, options.brick ?? NYC_PALETTE.brick, 0, h / 2, 0, w, h, depth);
  box(g, NYC_PALETTE.brickDark, 0, 0.35, 0, w + 0.1, 0.7, depth + 0.1);
  box(g, NYC_PALETTE.sandstone, 0, h - 0.28, 0, w + 0.32, 0.27, depth + 0.3);
  box(g, NYC_PALETTE.brickDark, 0, h + 0.03, 0, w + 0.16, 0.22, depth + 0.14);
  box(g, NYC_PALETTE.tealDark, -w * 0.23, 1.75, front + 0.08, 1.25, 2.3, 0.2);
  box(g, NYC_PALETTE.sandstone, -w * 0.23, 3.01, front + 0.12, 1.62, 0.24, 0.4);
  box(g, NYC_PALETTE.teal, -w * 0.23, 1.7, front + 0.22, 0.93, 1.8, 0.05);
  mesh(g, 'sphere', NYC_PALETTE.taxi, [-w * 0.23 + 0.35, 1.68, front + 0.27], [0.055, 0.055, 0.055]);
  for (let i = 0; i < 4; i++)
    box(
      g,
      NYC_PALETTE.sandstone,
      -w * 0.23,
      0.16 + i * 0.16,
      front + 1.1 - i * 0.3,
      1.9,
      0.32 + i * 0.32,
      0.65,
    );
  for (const side of [-1, 1])
    beam(
      g,
      NYC_PALETTE.iron,
      [-w * 0.23 + side, 0.8, front + 1.4],
      [-w * 0.23 + side, 1.65, front + 0.25],
      0.065,
    );
  for (let floor = 0; floor < Math.floor((h - 2.5) / 2.35); floor++) {
    const y = 4 + floor * 2.35;
    for (const x of [-w * 0.26, w * 0.26]) windowPanel(g, x, y, front + 0.04);
    if (floor === 0) flowerBox(g, -w * 0.26, y - 0.9, front + 0.38);
  }
  windowPanel(g, w * 0.26, 1.9, front + 0.03, 1, 1.4);
  if (options.fireEscape !== false) fireEscape(g, front, h, w * 0.25);
  // A few facade accents communicate brickwork without thousands of tiny bricks.
  for (let row = 0; row < 6; row++)
    for (let col = 0; col < 3; col++)
      box(
        g,
        NYC_PALETTE.brickLight,
        -w / 2 + 0.5 + (col * w) / 3 + (row % 2) * 0.2,
        1 + row * 1.15,
        front + 0.012,
        0.44,
        0.13,
        0.025,
      );
  return batch(g);
}

export type StoreKind = 'cafe' | 'bodega' | 'pizza';
export function createStorefront(kind: StoreKind = 'cafe'): THREE.Group {
  const g = group(kind);
  const w = kind === 'cafe' ? 6.7 : 6,
    depth = 5.6,
    front = depth / 2;
  const trim = kind === 'pizza' ? NYC_PALETTE.red : NYC_PALETTE.tealDark;
  box(g, kind === 'bodega' ? NYC_PALETTE.sandstone : NYC_PALETTE.brickLight, 0, 2.4, 0, w, 4.8, depth);
  box(g, NYC_PALETTE.brickDark, 0, 4.68, 0, w + 0.32, 0.32, depth + 0.3);
  box(g, trim, 0, 1.6, front + 0.08, w - 0.35, 2.9, 0.16);
  for (let i = 0; i < 3; i++) {
    const x = -w * 0.31 + i * w * 0.31;
    box(g, NYC_PALETTE.glassLight, x, 1.65, front + 0.18, w * 0.27, 2.3, 0.07);
    box(g, NYC_PALETTE.wood, x, 1.62, front + 0.24, 0.06, 2.3, 0.07);
    box(g, NYC_PALETTE.wood, x, 1.37, front + 0.24, w * 0.27, 0.07, 0.07);
    box(g, NYC_PALETTE.cream, x, 1.55, front + 0.14, 0.25, 0.35, 0.04, true);
  }
  box(g, trim, 0, 3.74, front + 0.1, w - 0.25, 0.78, 0.22);
  box(g, NYC_PALETTE.cream, 0, 3.74, front + 0.24, w - 0.57, 0.51, 0.05);
  // Text-free cafe, market and pizza pictograms. Host UI can add localized labels.
  if (kind === 'cafe') {
    cylinder(g, trim, 0, 3.72, front + 0.33, 0.19, 0.25);
    const handle = mesh(g, 'cylinder', NYC_PALETTE.cream, [0.23, 3.73, front + 0.33], [0.1, 0.06, 0.1]);
    handle.rotation.x = Math.PI / 2;
  } else if (kind === 'pizza') {
    const slice = mesh(g, 'pyramid', NYC_PALETTE.taxi, [0, 3.7, front + 0.37], [0.37, 0.48, 0.08]);
    slice.rotation.z = Math.PI;
    for (const x of [-0.12, 0.12])
      mesh(g, 'sphere', NYC_PALETTE.red, [x, 3.78, front + 0.43], [0.065, 0.065, 0.035]);
  } else
    for (let i = 0; i < 3; i++)
      mesh(
        g,
        'sphere',
        [NYC_PALETTE.red, NYC_PALETTE.taxi, NYC_PALETTE.foliage][i],
        [-0.3 + i * 0.3, 3.72, front + 0.36],
        [0.15, 0.15, 0.08],
      );
  const stripes = kind === 'pizza' ? 9 : 10;
  for (let i = 0; i < stripes; i++) {
    const awning = box(
      g,
      i % 2 ? NYC_PALETTE.cream : trim,
      -w / 2 + 0.12 + ((i + 0.5) * (w - 0.24)) / stripes,
      3.13,
      front + 0.67,
      (w - 0.24) / stripes + 0.005,
      0.13,
      1.5,
    );
    awning.rotation.x = 0.22;
    box(
      g,
      i % 2 ? NYC_PALETTE.cream : trim,
      awning.position.x,
      2.88,
      front + 1.38,
      (w - 0.24) / stripes,
      0.32,
      0.13,
    );
  }
  if (kind === 'bodega') {
    for (let crate = 0; crate < 3; crate++) {
      const x = -1.9 + crate * 1.28;
      box(g, NYC_PALETTE.wood, x, 0.48, front + 1.2, 1.15, 0.9, 0.85);
      for (let slat = 0; slat < 3; slat++)
        box(g, NYC_PALETTE.sandstone, x, 0.19 + slat * 0.25, front + 1.65, 1.1, 0.06, 0.03);
      for (let a = 0; a < 3; a++)
        for (let b = 0; b < 2; b++)
          mesh(
            g,
            'sphere',
            [NYC_PALETTE.red, NYC_PALETTE.taxi, NYC_PALETTE.foliageLight][crate],
            [x - 0.32 + a * 0.31, 1.01 + ((a + b) % 2) * 0.04, front + 1.03 + b * 0.32],
            [0.19, 0.2, 0.19],
          );
    }
  } else {
    const table = createCafeTable();
    table.position.set(-1.5, 0, front + 1.75);
    g.add(table);
    const planter = createPlanter();
    planter.position.set(w / 2 - 0.5, 0, front + 1.1);
    g.add(planter);
  }
  return batch(g);
}

export function createOffice(): THREE.Group {
  const g = group('office');
  box(g, NYC_PALETTE.sandstone, 0, 6.3, 0, 7, 12.6, 7);
  box(g, NYC_PALETTE.tealDark, 0, 0.65, 0, 7.1, 1.3, 7.1);
  for (let row = 0; row < 6; row++)
    for (let col = 0; col < 4; col++) {
      box(
        g,
        row % 2 ? NYC_PALETTE.glass : NYC_PALETTE.glassLight,
        -2.55 + col * 1.7,
        2 + row * 1.7,
        3.55,
        1.2,
        1.25,
        0.08,
      );
      box(g, NYC_PALETTE.glass, 3.55, 2 + row * 1.7, -2.55 + col * 1.7, 0.08, 1.25, 1.2);
    }
  for (let row = 0; row < 5; row++) box(g, NYC_PALETTE.cream, 0, 2.85 + row * 1.7, 0, 7.2, 0.1, 7.2);
  box(g, NYC_PALETTE.iron, 0, 12.65, 0, 7.2, 0.25, 7.2);
  box(g, NYC_PALETTE.tealDark, 0, 13, 0, 4.8, 0.6, 4.8);
  box(g, NYC_PALETTE.glassLight, 0, 1.1, 3.61, 1.6, 2.1, 0.1);
  box(g, NYC_PALETTE.iron, 0, 1.1, 3.68, 0.08, 2.1, 0.08);
  cylinder(g, NYC_PALETTE.wood, -1.6, 14.2, -1.2, 0.9, 1.8);
  mesh(g, 'cone', NYC_PALETTE.iron, [-1.6, 15.3, -1.2], [1.03, 0.5, 1.03]);
  for (const x of [-2.2, -1])
    for (const z of [-1.8, -0.6]) box(g, NYC_PALETTE.iron, x, 13.5, z, 0.09, 1, 0.09);
  return batch(g);
}

export function createCityHall(): THREE.Group {
  const g = group('city-hall');
  box(g, NYC_PALETTE.sandstone, 0, 2.7, -0.4, 6.6, 5.4, 5);
  box(g, NYC_PALETTE.cream, 0, 0.4, 0, 7, 0.8, 6.4);
  for (let i = 0; i < 3; i++)
    box(g, NYC_PALETTE.sandstone, 0, 0.14 + i * 0.14, 3.9 - i * 0.42, 5.8, 0.28 + i * 0.28, 0.8);
  for (const x of [-2.65, -0.9, 0.9, 2.65]) {
    cylinder(g, NYC_PALETTE.cream, x, 2.6, 2.65, 0.22, 3.7);
    box(g, NYC_PALETTE.cream, x, 0.85, 2.65, 0.65, 0.25, 0.65);
    box(g, NYC_PALETTE.cream, x, 4.35, 2.65, 0.65, 0.25, 0.65);
  }
  box(g, NYC_PALETTE.cream, 0, 4.7, 2.55, 7, 0.5, 1.4);
  const roof = mesh(g, 'pyramid', NYC_PALETTE.tealDark, [0, 5.55, -0.2], [5.1, 1.65, 4.5]);
  roof.rotation.y = Math.PI / 4;
  box(g, NYC_PALETTE.tealDark, 0, 2.1, 2.14, 1.5, 2.8, 0.14);
  for (const x of [-2, 2]) windowPanel(g, x, 2.5, 2.15, 0.85, 1.7);
  const clock = cylinder(g, NYC_PALETTE.cream, 0, 5.1, 3.16, 0.43, 0.09);
  clock.rotation.x = Math.PI / 2;
  box(g, NYC_PALETTE.iron, 0, 5.2, 3.23, 0.045, 0.23, 0.03);
  box(g, NYC_PALETTE.iron, 0.1, 5.1, 3.23, 0.24, 0.045, 0.03);
  return batch(g);
}

export function createTree(scale = 1): THREE.Group {
  const g = group('tree');
  cylinder(g, NYC_PALETTE.wood, 0, 1.2, 0, 0.14, 2.4);
  beam(g, NYC_PALETTE.wood, [0, 1.5, 0], [0.55, 2.3, 0.1], 0.1);
  mesh(g, 'leaf', NYC_PALETTE.foliage, [-0.35, 2.7, 0], [1.1, 1.05, 1.05]);
  mesh(g, 'leaf', NYC_PALETTE.foliageLight, [0.48, 2.85, 0.12], [0.9, 1, 0.9]);
  mesh(g, 'leaf', NYC_PALETTE.foliage, [0, 3.35, -0.12], [0.85, 0.8, 0.8]);
  g.scale.setScalar(scale);
  return batch(g);
}
export function createPlanter(): THREE.Group {
  const g = group('planter');
  cylinder(g, NYC_PALETTE.brick, 0, 0.35, 0, 0.35, 0.7);
  cylinder(g, NYC_PALETTE.sandstone, 0, 0.67, 0, 0.4, 0.12);
  for (let i = 0; i < 3; i++)
    mesh(
      g,
      'leaf',
      i % 2 ? NYC_PALETTE.foliageLight : NYC_PALETTE.foliage,
      [Math.sin(i * 2.1) * 0.2, 1 + i * 0.12, Math.cos(i * 2.1) * 0.17],
      [0.32, 0.5, 0.32],
    );
  return g;
}
export function createCafeTable(): THREE.Group {
  const g = group('cafe-table');
  cylinder(g, NYC_PALETTE.wood, 0, 0.93, 0, 0.57, 0.12);
  cylinder(g, NYC_PALETTE.iron, 0, 0.46, 0, 0.06, 0.85);
  cylinder(g, NYC_PALETTE.iron, 0, 0.06, 0, 0.31, 0.08);
  for (const side of [-1, 1]) {
    box(g, NYC_PALETTE.teal, side * 0.83, 0.53, 0, 0.47, 0.1, 0.5);
    box(g, NYC_PALETTE.teal, side * 1.02, 0.92, 0, 0.1, 0.7, 0.5);
    for (const z of [-0.16, 0.16]) box(g, NYC_PALETTE.iron, side * 0.83, 0.26, z, 0.06, 0.51, 0.06);
  }
  cylinder(g, NYC_PALETTE.cream, 0.12, 1.08, 0, 0.1, 0.18);
  return g;
}
export function createBench(): THREE.Group {
  const g = group('bench');
  for (let i = 0; i < 3; i++) box(g, NYC_PALETTE.wood, 0, 0.55, -0.2 + i * 0.2, 1.9, 0.1, 0.16);
  for (let i = 0; i < 2; i++) box(g, NYC_PALETTE.wood, 0, 0.96 + i * 0.22, -0.35, 1.9, 0.17, 0.08);
  for (const x of [-0.7, 0.7]) {
    box(g, NYC_PALETTE.iron, x, 0.29, 0, 0.1, 0.58, 0.5);
    box(g, NYC_PALETTE.iron, x, 0.86, -0.37, 0.08, 0.8, 0.1);
  }
  return batch(g);
}
export function createStreetLamp(): THREE.Group {
  const g = group('street-lamp');
  cylinder(g, NYC_PALETTE.tealDark, 0, 1.6, 0, 0.065, 3.2);
  cylinder(g, NYC_PALETTE.tealDark, 0, 0.15, 0, 0.17, 0.3);
  mesh(g, 'sphere', NYC_PALETTE.cream, [0, 3.35, 0], [0.24, 0.27, 0.24], true);
  return batch(g);
}
export function createSubwayEntrance(): THREE.Group {
  const g = group('subway');
  box(g, NYC_PALETTE.iron, 0, 0.022, 0, 3.3, 0.04, 3.2);
  for (let i = 0; i < 5; i++) box(g, 0x8f9a91, 0, 0.05, 1.25 - i * 0.52, 2.5, 0.07, 0.34);
  for (const side of [-1, 1]) {
    for (let i = 0; i < 6; i++)
      box(g, NYC_PALETTE.tealDark, side * 1.55, 0.62, -1.4 + i * 0.56, 0.07, 1.2, 0.07);
    box(g, NYC_PALETTE.tealDark, side * 1.55, 1.18, 0, 0.12, 0.12, 3.4);
    box(g, NYC_PALETTE.tealDark, side * 1.55, 0.27, 0, 0.1, 0.12, 3.4);
    cylinder(g, NYC_PALETTE.tealDark, side * 1.55, 0.9, 1.65, 0.14, 1.8);
    mesh(g, 'sphere', NYC_PALETTE.cream, [side * 1.55, 1.95, 1.65], [0.26, 0.26, 0.26], true);
  }
  box(g, NYC_PALETTE.tealDark, 0, 1.18, -1.55, 3.2, 0.12, 0.12);
  box(g, NYC_PALETTE.tealDark, 0, 1.5, -1.55, 2.8, 0.45, 0.12);
  for (let i = 0; i < 3; i++)
    mesh(
      g,
      'sphere',
      [NYC_PALETTE.red, NYC_PALETTE.taxi, NYC_PALETTE.glassLight][i],
      [-0.55 + i * 0.55, 1.5, -1.46],
      [0.13, 0.13, 0.04],
    );
  return batch(g);
}
export function createTaxi(): THREE.Group {
  const g = group('taxi');
  box(g, NYC_PALETTE.taxi, 0, 0.65, 0, 3.5, 0.7, 1.5);
  box(g, NYC_PALETTE.taxi, -0.15, 1.13, 0, 1.85, 0.72, 1.36);
  box(g, NYC_PALETTE.glass, 0.1, 1.15, 0.7, 1.45, 0.42, 0.04);
  box(g, NYC_PALETTE.glass, 0.1, 1.15, -0.7, 1.45, 0.42, 0.04);
  box(g, NYC_PALETTE.taxi, 0.05, 1.15, 0, 0.1, 0.55, 1.48);
  box(g, NYC_PALETTE.glass, 0.83, 1.12, 0, 0.06, 0.47, 1.26);
  box(g, NYC_PALETTE.glass, -1.08, 1.12, 0, 0.06, 0.47, 1.26);
  box(g, NYC_PALETTE.cream, -0.1, 1.57, 0, 0.55, 0.19, 0.31);
  box(g, NYC_PALETTE.iron, 1.79, 0.6, 0, 0.08, 0.28, 0.72);
  for (const z of [-0.55, 0.55]) box(g, NYC_PALETTE.cream, 1.81, 0.7, z, 0.06, 0.2, 0.23, true);
  for (const x of [-1.12, 1.12])
    for (const z of [-0.76, 0.76]) {
      const wheel = cylinder(g, NYC_PALETTE.iron, x, 0.39, z, 0.36, 0.17);
      wheel.rotation.x = Math.PI / 2;
      const hub = cylinder(g, 0xb5b6a6, x, 0.39, z * 1.05, 0.17, 0.19);
      hub.rotation.x = Math.PI / 2;
    }
  return batch(g);
}

export function createBridge(): THREE.Group {
  const g = group('distant-bridge');
  box(g, 0xb19b80, 0, 1.55, 0, 47, 0.4, 2.4);
  for (const x of [-12, 12]) {
    for (const z of [-0.85, 0.85]) box(g, 0xbea187, x, 4.5, z, 0.75, 8, 0.65);
    box(g, 0xbea187, x, 7.6, 0, 0.95, 1, 2.5);
    box(g, 0xbea187, x, 5.4, 0, 0.85, 0.5, 2.3);
  }
  for (const side of [-1, 1]) {
    const z = side * 1.12;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= 32; i++) {
      const x = -23 + (i * 46) / 32;
      const y = Math.abs(x) <= 12 ? 2.7 + 4.6 * (x / 12) ** 2 : 7.3 - (Math.abs(x) - 12) * 0.43;
      points.push(new THREE.Vector3(x, y, z));
      if (i % 2 === 0) beam(g, 0x887d6f, [x, 1.75, z], [x, y, z], 0.025);
    }
    const cable = new THREE.Mesh(
      new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 48, 0.055, 4, false),
      mat(0x887d6f),
    );
    g.add(cable);
  }
  return batch(g);
}

/** Named shoulder/hip pivots permit walking animation without a skeleton. */
export function createAvatar(options: AvatarOptions = {}): THREE.Group {
  const g = group('avatar');
  const skin = options.skin ?? 0xc58a63;
  const hair = options.hair ?? 0x49382e,
    shirt = options.shirt ?? NYC_PALETTE.teal;
  const trousers = options.trousers ?? 0x4c5559;
  const torso = box(g, shirt, 0, 1.15, 0, 0.58, 0.65, 0.34);
  torso.name = 'torso';
  cylinder(g, skin, 0, 1.52, 0, 0.105, 0.13);
  const head = group('head');
  head.position.set(0, 1.77, 0);
  g.add(head);
  mesh(head, 'head', skin, [0, 0, 0], [0.28, 0.32, 0.27]);
  mesh(head, 'hair', hair, [0, 0.025, -0.015], [0.295, 0.32, 0.285]);
  box(head, hair, -0.245, 0.015, -0.015, 0.08, 0.23, 0.29);
  for (const x of [-0.095, 0.095]) mesh(head, 'sphere', 0x332d29, [x, 0.015, 0.251], [0.024, 0.03, 0.023]);
  box(head, 0x6c4938, 0, -0.1, 0.257, 0.08, 0.017, 0.018);
  for (const side of [-1, 1]) {
    const arm = group(side < 0 ? 'leftArm' : 'rightArm');
    arm.position.set(side * 0.36, 1.43, 0);
    g.add(arm);
    const sleeve = cylinder(arm, shirt, 0, -0.16, 0, 0.105, 0.31);
    sleeve.name = 'sleeve';
    cylinder(arm, skin, 0, -0.39, 0, 0.08, 0.27);
    mesh(arm, 'sphere', skin, [0, -0.56, 0], [0.09, 0.105, 0.085]);
    const leg = group(side < 0 ? 'leftLeg' : 'rightLeg');
    leg.position.set(side * 0.145, 0.79, 0);
    g.add(leg);
    box(leg, trousers, 0, -0.32, 0, 0.23, 0.65, 0.27);
    box(leg, NYC_PALETTE.cream, 0, -0.69, 0.055, 0.25, 0.14, 0.39);
  }
  g.userData.height = 2.1;
  g.userData.forward = '+Z';
  g.userData.animationParts = ['leftArm', 'rightArm', 'leftLeg', 'rightLeg', 'head'];
  return g;
}

export function createNewYorkScene(options: { distantSkyline?: boolean } = {}): THREE.Group {
  const root = group('new-york-scene');
  root.userData.map = NYC_MAP;
  const ground = group('ground-and-roads');
  box(ground, NYC_PALETTE.sidewalk, 0, -0.16, -2, 53, 0.3, 33);
  box(ground, NYC_PALETTE.asphalt, 0, -0.005, 8.7, 53, 0.055, 5.8);
  for (const z of [5.75, 11.65]) box(ground, NYC_PALETTE.cream, 0, 0.025, z, 53, 0.13, 0.18);
  for (let x = -24; x <= 24; x += 4) box(ground, NYC_PALETTE.taxi, x, 0.028, 8.7, 1.8, 0.025, 0.09);
  for (let i = 0; i < 7; i++) box(ground, NYC_PALETTE.cream, -1.8 + i * 0.62, 0.035, 8.7, 0.34, 0.025, 4.6);
  for (let x = -24; x <= 24; x += 3) box(ground, 0xbeb29d, x, 0.002, 0.8, 0.025, 0.025, 9.6);
  for (const z of [-3.7, -0.5, 2.7]) box(ground, 0xbeb29d, 0, 0.003, z, 51, 0.025, 0.025);
  box(ground, NYC_PALETTE.river, 0, -0.27, -24.7, 74, 0.3, 17);
  for (let i = 0; i < 16; i++)
    box(ground, i % 2 ? 0x91bac0 : 0x689ba6, -31 + i * 4.1, -0.105, -20.5 - (i % 4) * 2.1, 2.8, 0.014, 0.09);
  box(ground, NYC_PALETTE.sandstone, 0, -0.04, -15, 53, 0.24, 2.2);
  // Waterfront railing; promenade connects to the east/west edge lanes.
  box(ground, NYC_PALETTE.tealDark, 0, 0.8, -16.1, 53, 0.08, 0.08);
  for (let x = -26; x <= 26; x += 1.3) box(ground, NYC_PALETTE.tealDark, x, 0.42, -16.1, 0.05, 0.82, 0.05);
  root.add(batch(ground));
  const place = (asset: THREE.Group, x: number, z: number, name?: string, angle = 0): THREE.Group => {
    asset.position.set(x, 0, z);
    asset.rotation.y = angle;
    if (name) asset.name = name;
    root.add(asset);
    return asset;
  };
  place(createBrownstone({ height: 9.2, brick: NYC_PALETTE.brickDark }), -19.3, -9.1, 'brownstone-west');
  place(createBrownstone({ height: 10.4 }), -13.7, -9.1, 'home');
  place(createStorefront('cafe'), -7.1, -8.9, 'cafe');
  place(createStorefront('bodega'), -0.2, -8.9, 'bodega');
  place(createStorefront('pizza'), 6.2, -8.9, 'pizza');
  place(createOffice(), 15.2, -9.7, 'jobs');
  place(createCityHall(), -20.8, 1.8, 'city-hall', Math.PI / 2);
  place(createSubwayEntrance(), 2, 0, 'subway');
  const park = group('pocket-park');
  box(park, 0x99a56e, 0, -0.025, 0, 9.5, 0.14, 6.1);
  box(park, NYC_PALETTE.sandstone, 0, 0.055, 0.5, 1.65, 0.035, 5.1);
  box(park, NYC_PALETTE.sandstone, 0, 0.055, 1.6, 8.8, 0.035, 1.2);
  place(batch(park), 13.5, 1.8);
  place(createTree(1.12), 10.2, 1.8);
  place(createTree(1.15), 16.7, 1);
  place(createTree(0.9), 16.8, 4.2);
  place(createBench(), 10.5, 4.4);
  place(createBench(), 16.7, 4.6);
  for (const x of [-15.7, -4.2, 7.8, 21.3]) place(createStreetLamp(), x, 5.1);
  for (const [x, z] of [
    [-23, 9],
    [-4, 2.8],
    [21.5, 1.6],
  ])
    place(batch(createPlanter()), x, z);
  const taxi = place(createTaxi(), -8, 9.5, 'taxi');
  taxi.userData.dynamic = true;
  place(createBridge(), 0, -24, 'bridge');
  if (options.distantSkyline !== false) {
    const skyline = group('distant-skyline');
    const heights = [6, 9, 7.5, 12, 8.5, 15, 10, 7, 11, 8];
    heights.forEach((height, i) => {
      const x = -28 + i * 6.1;
      box(skyline, i % 2 ? 0x9eb6bd : 0x88a6b1, x, height / 2 - 0.3, -34 - (i % 3) * 2, 4.6, height, 4.7);
      if (i === 5) {
        box(skyline, 0x88a6b1, x, height + 0.4, -34 - (i % 3) * 2, 2, 1.8, 2);
        cylinder(skyline, 0x88a6b1, x, height + 2.5, -34 - (i % 3) * 2, 0.12, 3);
      }
    });
    root.add(batch(skyline));
  }
  root.userData.cameraSuggestion = { position: [30, 29, 34], lookAt: [0, 1.5, -3], orthographicSpan: 45 };
  root.userData.assetNotes =
    'Procedural original scenery; text labels belong to localized host UI. NPC/player characters are added separately.';
  return root;
}

/** Union-of-rectangles movement helper. Host remains authoritative for gameplay. */
export function createCommunityGarden(stage: number): THREE.Group {
  const root = group('community-garden');
  for (let row = 0; row < 2; row++)
    for (let col = 0; col < 2; col++) {
      const x = 11.5 + col * 4,
        z = 0.05 + row * 3.35;
      box(root, NYC_PALETTE.wood, x, 0.13, z, 1.65, 0.22, 0.98);
      box(root, 0x685342, x, 0.25, z, 1.5, 0.03, 0.83);
      if (stage >= 1)
        for (let i = 0; i < 6; i++) {
          const px = x - 0.47 + (i % 3) * 0.47,
            pz = z - 0.22 + Math.floor(i / 3) * 0.44;
          cylinder(root, 0x607d49, px, 0.42, pz, 0.025, 0.32);
          mesh(root, 'leaf', stage >= 2 ? 0x819451 : 0x9ab667, [px, 0.58, pz], [0.19, 0.22, 0.17]);
          if (stage >= 2)
            mesh(root, 'leaf', [0xe5b768, 0xc7848e, 0xf0dd9f][i % 3]!, [px, 0.79, pz], [0.12, 0.13, 0.12]);
        }
    }
  if (stage >= 3) {
    for (const x of [11.1, 15.9]) cylinder(root, NYC_PALETTE.wood, x, 1.3, 2.05, 0.055, 2.6);
    beam(root, NYC_PALETTE.iron, [11.1, 2.6, 2.05], [15.9, 2.6, 2.05], 0.022);
    for (let i = 0; i < 8; i++)
      mesh(
        root,
        'sphere',
        i % 2 ? 0xe6c478 : 0xb7c99b,
        [11.45 + i * 0.6, 2.46, 2.05],
        [0.07, 0.11, 0.07],
        true,
      );
  }
  if (stage >= 4) {
    box(root, NYC_PALETTE.teal, 13.5, 1.55, 2.05, 1.55, 0.45, 0.07);
    for (let i = 0; i < 4; i++)
      box(root, [0xc7848e, 0xe5b768, 0x94b0a0, 0xd8b48b][i]!, 12.15 + i * 0.9, 2.16, 2.05, 0.38, 0.48, 0.055);
  }
  root.userData.stage = stage;
  return batch(root);
}

export function isNewYorkWalkable(x: number, z: number, radius = 0.28): boolean {
  const inside = NYC_MAP.walkable.some(
    (r) => x >= r.minX + radius && x <= r.maxX - radius && z >= r.minZ + radius && z <= r.maxZ - radius,
  );
  if (!inside) return false;
  return !NYC_MAP.blocked.some(
    (r) => x + radius > r.minX && x - radius < r.maxX && z + radius > r.minZ && z - radius < r.maxZ,
  );
}

/**
 * Assets share cached geometries/materials. When unloading one scene, dispose only
 * its InstancedMesh GPU resources (and unique TubeGeometry); do not dispose shared
 * materials/geometries while another kit scene/avatar is still mounted.
 */
